import React, { useState, useEffect } from "react";
import { useToast } from "../hooks/use-toast.ts";
import { Card } from "./ui/card.tsx";
import { Button } from "./ui/button.tsx";
import { Input } from "./ui/input.tsx";
import { supabase } from "../integrations/supabase/client.ts";
import { fetchSubscriptions } from "../data/subscriptionData.ts";
import {
  getOfferPageById,
  updateOfferPageProducts,
  updateOfferPageDetails,
} from "../services/offerPageService.ts";
import { fetchProducts } from "../services/productService.ts";
import { X } from "lucide-react";
import NoProductsWarning from "./offer/NoProductsWarning.tsx";
import { Label } from "./ui/label.tsx";
import UnifiedProductSelectionGrid from "./offer/UnifiedProductSelectionGrid.tsx";
import { Badge } from "./ui/badge.tsx";
import { useGlobalEvents } from "@/hooks/useGlobalEvents.ts";
import { Item } from "../utils/dashboardDataUtils.ts";
import { Textarea } from "./ui/textarea.tsx";

interface EditOfferFormProps {
  offerPageId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditOfferForm: React.FC<EditOfferFormProps> = ({
  offerPageId,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [heading, setHeading] = useState("");
  const [description, setDescription] = useState("");

  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [currentProductIds, setCurrentProductIds] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { dispatchOfferCreated } = useGlobalEvents();

  useEffect(() => {
    const loadOfferData = async () => {
      try {
        setLoading(true);
        const { data: userData, error: userError } =
          await supabase.auth.getUser();
        if (userError || !userData.user) {
          toast({
            title: "Error",
            description: "User not authenticated.",
            variant: "destructive",
          });
          onClose();
          return;
        }
        setUserId(userData.user.id);

        const offerPage = await getOfferPageById(offerPageId);
        if (!offerPage) {
          toast({
            title: "Error",
            description: "Offer page not found.",
            variant: "destructive",
          });
          onClose();
          return;
        }

        setHeading(offerPage.heading || "");
        setDescription(offerPage.subheading || "");

        const productsIds =
          offerPage.products?.map(
            (product: { product_id: string }) => product.product_id
          ) || [];
        setCurrentProductIds(productsIds);

        const [products, subsciptions] = await Promise.all([
          fetchProducts(),
          fetchSubscriptions(),
        ]);

        const userProducts = products.filter(
          (product) =>
            product.user_id === userData.user.id && product.status === "active"
        );

        const userSubscriptions = subsciptions.filter(
          (subscription) =>
            subscription.user_id === userData.user.id &&
            subscription.status === "active"
        );

        const allItems = [...userProducts, ...userSubscriptions];
        setAvailableItems(allItems);

        const currentlySelected = allItems.filter((item) =>
          productsIds.includes(item.id)
        );
        setSelectedItems(currentlySelected);
      } catch (error) {
        console.error("Error loading bundle data:", error);
        toast({
          title: "Error",
          description: "Failed to load bundle data.",
          variant: "destructive",
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };
    if (offerPageId) {
      loadOfferData();
    }
  }, [offerPageId, onClose, toast]);

  const handleItemSelect = (item: Item) => {
    setSelectedItems((prev) => {
      const isAlreadySelected = prev.some((p) => p.id === item.id);
      const hasDifferentBillingPeriod = prev.some(
        (p) =>
          p.billing_period !== item.billing_period &&
          p.billing_period !== "one_time" &&
          item.billing_period !== "one_time"
      );

      if (hasDifferentBillingPeriod) {
        toast({
          title: "Error",
          description: "Cannot mix different billing periods.",
          variant: "destructive",
        });
        return prev;
      }
      return isAlreadySelected
        ? prev.filter((p) => p.id !== item.id)
        : [...prev, item];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast({
        title: "Error",
        description: "User not authenticated.",
        variant: "destructive",
      });
      return;
    }
    if (selectedItems.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one item.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Update offer page details (heading and description)
      await updateOfferPageDetails(offerPageId, {
        heading,
        description,
      });

      // Update products
      await updateOfferPageProducts(
        offerPageId,
        selectedItems.map((item) => item.id)
      );

      toast({
        title: "Success",
        description: "Offer page updated successfully.",
        variant: "success",
      });
      
      // Dispatch the offer created event to update any listeners
      dispatchOfferCreated(offerPageId);
      
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error updating bundle page:", error);
      toast({
        title: "Error",
        description: "Failed to update bundle page.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getChangeSummary = () => {
    const originalIds = new Set(currentProductIds);
    const newIds = new Set(selectedItems.map((item) => item.id));

    const added = selectedItems.filter((item) => !originalIds.has(item.id));
    const removed = currentProductIds.filter((id) => !newIds.has(id));
    return { added, removed: removed.length };
  };

  const getItemName = (item: Item) => {
    return item.name || item.title || "Unnamed item";
  };

  // Note: Using 'any' type due to TypeScript DOM configuration issues
  const handleHeadingChange = (e: any) => {
    setHeading(e.target.value);
  };

  const handleDescriptionChange = (e: any) => {
    setDescription(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full max-h-[100vh] p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-3xl font-semibold">Edit Bundle Page</h2>
        </div>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading bundle page data...</p>
        </div>
      </div>
    );
  }

  if (!loading && availableItems.length === 0) {
    return <NoProductsWarning offerId="no-bundle" productCount={0} />;
  }

  const { added, removed } = getChangeSummary();

  return (
    <div className="flex flex-col h-full max-h-[100vh]">
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        {/* header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2 className="text-3xl font-semibold">Edit Bundle Page</h2>
          </div>
        </div>

        {/* scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pb-24">
          {/* offer details form */}
          <Card className="p-6 mb-4 bg-[#F2F2F2]">
            <h3 className="text-lg font-semibold mb-4">Bundle page details</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="heading">Heading</Label>
                <Input
                  id="heading"
                  placeholder="e.g. Summere Savings Event"
                  value={heading}
                  onChange={handleHeadingChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="e.g. Get ready for summer savings!"
                  value={description}
                  onChange={handleDescriptionChange}
                  required
                />
              </div>
            </div>
          </Card>

          {/* Change Summary */}
          {(added.length > 0 || removed > 0) && (
            <Card className="p-6 mb-4 bg-[#F2F2F2]">
              <h3 className="text-lg font-semibold mb-4">Change Summary</h3>
              <div className="space-y-2">
                {added.length > 0 && (
                  <div>
                    <strong>Added:</strong>
                    <ul>
                      {added.map((item) => (
                        <li key={item.id}>{getItemName(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {removed > 0 && (
                  <div>
                    <strong>Removed:</strong>
                    <p>{removed} product(s)</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* products selection */}
          <UnifiedProductSelectionGrid
            availableItems={availableItems}
            selectedItems={selectedItems}
            onItemSelect={handleItemSelect}
            loading={loading}
          />
        </div>

        {/* footer */}
        <div className="fixed bottom-0 left-0 right-0  bg-white border-t border-gray-50 pt-4 pb-4">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center gap-4 mb-2">
              <p className="text-sm text-muted-foreground">
                {selectedItems.length} item
                {selectedItems.length !== 1 ? "s" : ""} selected
              </p>
              {(added.length > 0 || removed > 0) && (
                <Badge variant="outline" className="text-xs">
                  {added.length + removed} change
                  {added.length + removed > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            <div className="flex gap-2 w-full p-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
                onClick={onClose}
              >
                Discard Changes
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting || selectedItems.length === 0}
              >
                {isSubmitting ? "Updating Bundle..." : "Update Bundle"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditOfferForm;
